/**
 * The runner's picture of a run: the server's last answer with this phone's unsent ticks laid over
 * it, matched to the published content. Works with no run view at all (just started offline).
 */
import {
  PROCEDURES_RUN_STEP_STATUS,
  type ProcedurePortalView,
  type ProcedureRunView,
  type ProcedureStepView,
} from '@actschurch/ccms-sdk';
import { stampToDate } from './format';
import type { QueueEntry } from './syncQueue';

export interface RunnerStep {
  stepID: number;
  /** 1-based position in this run ("Step 3"). */
  number: number;
  title: string;
  /** The published content (null when the step changed since the run started). */
  content: ProcedureStepView | null;
  statusID: number | null;
  checkedIds: number[];
  note: string | null;
  checkedAt: Date | null;
  /** True while this step's latest change is still on the phone only. */
  pending: boolean;
}

export interface RunnerModel {
  steps: RunnerStep[];
  done: number;
  skipped: number;
  problems: number;
  /** Steps with any status. */
  answered: number;
  total: number;
  /** 0-based index of the first step without a status (or the last step). */
  firstOpenIndex: number;
  /** True when a finish (complete) is waiting to sync. */
  finishPending: boolean;
  abandonPending: boolean;
}

export function buildRunnerModel(
  portal: ProcedurePortalView | null | undefined,
  run: ProcedureRunView | null | undefined,
  pending: QueueEntry[],
): RunnerModel {
  const contentById = new Map<number, ProcedureStepView>();
  const portalSteps = [...(portal?.steps ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.stepNumber - b.stepNumber);
  portalSteps.forEach((s) => contentById.set(s.stepID, s));

  let steps: RunnerStep[];
  if (run && run.steps.length > 0) {
    steps = [...run.steps]
      .sort((a, b) => a.stepNumber - b.stepNumber)
      .map((s, i) => ({
        stepID: s.stepID,
        number: i + 1,
        title: contentById.get(s.stepID)?.title ?? s.title,
        content: contentById.get(s.stepID) ?? null,
        statusID: s.statusID ?? null,
        checkedIds: s.checkedIds ?? [],
        note: s.note ?? null,
        checkedAt: stampToDate(s.checkedAt ?? null),
        pending: false,
      }));
  } else {
    steps = portalSteps.map((s, i) => ({
      stepID: s.stepID,
      number: i + 1,
      title: s.title,
      content: s,
      statusID: null,
      checkedIds: [],
      note: null,
      checkedAt: null,
      pending: false,
    }));
  }

  const index = new Map<number, RunnerStep>();
  steps.forEach((s) => index.set(s.stepID, s));
  let finishPending = false;
  let abandonPending = false;
  for (const e of pending) {
    if (e.kind === 'complete') finishPending = true;
    if (e.kind === 'abandon') abandonPending = true;
    if (e.kind !== 'step' || e.stepId === undefined || !e.step) continue;
    const s = index.get(e.stepId);
    if (!s) continue;
    s.statusID = e.step.statusID ?? null;
    s.checkedIds = e.step.checkedIds ?? [];
    s.note = e.step.note ?? null;
    s.checkedAt = e.step.statusID ? new Date(e.createdAt) : null;
    s.pending = true;
  }

  let done = 0;
  let skipped = 0;
  let problems = 0;
  let answered = 0;
  for (const s of steps) {
    if (s.statusID) answered += 1;
    if (s.statusID === PROCEDURES_RUN_STEP_STATUS.Done) done += 1;
    if (s.statusID === PROCEDURES_RUN_STEP_STATUS.Skipped) skipped += 1;
    if (s.statusID === PROCEDURES_RUN_STEP_STATUS.Problem) problems += 1;
  }
  const open = steps.findIndex((s) => !s.statusID);
  return {
    steps,
    done,
    skipped,
    problems,
    answered,
    total: steps.length,
    firstOpenIndex: open >= 0 ? open : Math.max(0, steps.length - 1),
    finishPending,
    abandonPending,
  };
}
